import * as Yup from 'yup';
import axios from '@/utils/api/axios';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBoolean } from '@/hooks/state/use-boolean';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from '@/components/feedback/snackbar';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogError } from '@/components/utils/errors/dialogs/dialog-error';
import FormProvider, {
  RHFSelect,
  RHFTextField,
  RHFCheckbox,
  RHFNumberField,
  RHFAutocomplete,
} from '@/components/forms/hook-form';
import {
  Box,
  Card,
  Button,
  MenuItem,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import {
  IOrderData,
  OrderTypes,
  ISalesOrder,
  MilestoneData,
  IOrderDocument,
  ChoicesOrderStatus,
  IOrderItem as IOrder,
  ISalesBillingOrderItem,
  IBillingOrderCurrentItem,
} from '@/types';
import { IOrganizationItem } from '@/types/organization';
import Iconify from '@/components/media/iconify';
import { ShowIf } from '@/utils';
import { BillingOrderDocument } from '@/components/data-display/billing-milestones/types';
import { getTotal } from '@/utils/billing/billing-milestones-snackbar';
import { ChoicesOrderUnitMeasure } from '@/sections/administrative/accounts/orders/context';
import { createBillingOrderSecondStepSchema } from '../schemas/billing-order-second-step-schema';
import OrderUploadOVDialog from '@/sections/administrative/accounts/orders/forms/documents/order-upload-ov';
import { OrderLegalEntityNewEditForm } from '@/sections/administrative/accounts/orders/forms/order-create-legal-entity';
import { BillingOrderLegalEntityBillingItem } from './components/billing-order-legal-entity-billing-item';

interface FormError {
  message: string;
  code?: string;
  details?: string;
}
interface TransformedItem {
  value: number;
  subAmount: number;
  totalDiscount: number;
  amount: number;
  itemId: string;
  subscription: string;
  milestones: string[];
  unitMeasure: ChoicesOrderUnitMeasure;
}

interface TransformedLegalEntity extends Entity {
  subAmount: number;
  totalDiscount: number;
  totalAmount: number;
  items: TransformedItem[];
}

interface EquivalenceError {
  hasError: boolean;
  itemIndex: number;
}

interface Entity {
  _id: string;
  label: string;
  legal_name: string;
  tax_number: string;
  documents: BillingOrderDocument[];
}

type Props = {
  currentOrder: IOrder & {
    orderDiscounts?: { amount: number; description: string }[];
  };
  currentOrganization: IOrganizationItem;
  currentSalesOrder?: ISalesOrder[];
  onNext: (data: Partial<IOrderData>) => void;
  onBack: () => void;
  onReset: () => void;
  onCancel: () => void;
  milestonesResetTrigger?: number;
};

const checkEquivalenceBetweenItems = (
  salesItems: ISalesBillingOrderItem[],
  orderItems: IBillingOrderCurrentItem[]
): EquivalenceError[] => {
  const errors: EquivalenceError[] = [];

  const sumAmounts = (data: ISalesBillingOrderItem[]): number[] =>
    data.reduce((acc, item) => {
      item.amount.forEach((amt, idx) => {
        acc[idx] = (acc[idx] || 0) + amt;
      });
      return acc;
    }, [] as number[]);

  const totalSalesItems = sumAmounts(salesItems);

  orderItems.forEach((item, index) => {
    if (!item?.milestone?.total) {
      return;
    }

    const milestoneTotal: number = item.milestone.total;
    const totalSaleItem: number = +totalSalesItems[index];
    if (milestoneTotal.toFixed(2) !== totalSaleItem.toFixed(2)) {
      errors.push({ hasError: true, itemIndex: index + 1 });
    }
  });

  return errors;
};

interface FormData {
  legalEntity: Entity[];
  items: ISalesBillingOrderItem[];
  unitMeasure: string[];
  onlySave?: boolean;
  enableDiscounts?: boolean;
  discounts?: {
    amount: number;
    description: string;
  }[];
  currentDiscount?: {
    amount: number;
    description: string;
  };
}

const transformInputData = (data: FormData): TransformedLegalEntity[] => {
  const totalAmount = data.legalEntity.reduce((total, entity, entityIndex) => {
    const itemData = data.items[entityIndex];
    if (!itemData) return total;

    return total + itemData.amount.reduce((sum: number, amt: number) => sum + (amt || 0), 0);
  }, 0);

  const totalDiscountAmount =
    data.enableDiscounts && data.discounts
      ? data.discounts.reduce((sum, discount) => sum + (discount.amount || 0), 0)
      : 0;

  const discountRatio = totalAmount > 0 ? totalDiscountAmount / totalAmount : 0;

  const result = data.legalEntity.map((entity: Entity, index: number) => {
    const itemData = data.items[index];

    if (!itemData) {
      throw new Error(`No item data found for the index ${index}`);
    }

    const { value, amount, itemId, subscription, milestones } = itemData;

    const entitySubtotal = amount.reduce((sum: number, amt: number) => sum + (amt || 0), 0);

    const entityDiscountAmount = entitySubtotal * discountRatio;

    return {
      _id: entity._id,
      label: entity.label,
      legal_name: entity.legal_name,
      tax_number: entity.tax_number,
      documents: entity.documents || [],
      subAmount: entitySubtotal,
      totalDiscount: entityDiscountAmount,
      totalAmount: entitySubtotal - entityDiscountAmount,
      items: value.map((itemValue: number, itemIndex: number) => {
        const originalAmount = amount[itemIndex];
        const itemDiscountAmount = originalAmount * discountRatio;
        const finalAmount = originalAmount - itemDiscountAmount;

        return {
          value: itemValue,
          subAmount: originalAmount,
          totalDiscount: itemDiscountAmount,
          amount: finalAmount,
          itemId: itemId[itemIndex],
          subscription: subscription[itemIndex],
          milestones: milestones[itemIndex],
          unitMeasure: data.unitMeasure[itemIndex] as ChoicesOrderUnitMeasure,
        };
      }),
    };
  });

  return result;
};

const extractFirstItemValues = (
  data: TransformedLegalEntity[],
  itemsCurrentOrder: IBillingOrderCurrentItem[]
): ISalesBillingOrderItem[] => {
  const maxLength = itemsCurrentOrder.length;
  return data.map(({ items }) => {
    const slicedItems = items.slice(0, maxLength);
    return {
      value: slicedItems.map((item: TransformedItem) => item.value ?? 0),
      amount: slicedItems.map((item: TransformedItem) => item.subAmount ?? item.amount ?? 0),
      itemId: slicedItems.map(
        (item: TransformedItem, i: number) => item.itemId || itemsCurrentOrder[i]?._id || ''
      ),
      subscription: slicedItems.map(
        (item: TransformedItem, i: number) =>
          item.subscription || itemsCurrentOrder[i]?.subscription || ''
      ),
      milestones: slicedItems.map((item: TransformedItem, i: number) => {
        if (item.milestones?.length > 0) {
          return item.milestones;
        }
        return itemsCurrentOrder[i]?.allMilestones?.map((m: MilestoneData) => m._id) || [];
      }),
    };
  });
};

//TODO: Remove this component and their children after the QA Validations
export const BillingOrderNewEditSecondStepForm = ({
  currentOrder,
  currentOrganization,
  currentSalesOrder,
  onNext,
  onBack,
  onReset,
  onCancel,
  milestonesResetTrigger = 0,
}: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState<FormError | null>(null);
  const dialogError = useBoolean();
  const openUploadFile = useBoolean();
  const openCreateLegalEntity = useBoolean();
  const [documentIndex, setDocumentIndex] = useState<number>(0);
  const [documentLegalEntityId, setDocumentLegalEntityId] = useState<string | null>(null);
  const [currentEditingDocument, setCurrentEditingDocument] = useState<any>(null);
  const [prevLegalEntityCount, setPrevLegalEntityCount] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Record<string, string[]>>({});

  const toggleRow = (legalEntityId: string, subscriptionId: string) => {
    setExpandedRows((prev) => {
      const currentExpanded = prev[legalEntityId] || [];
      const isExpanded = currentExpanded.includes(subscriptionId);

      return {
        ...prev,
        [legalEntityId]: isExpanded
          ? currentExpanded.filter((id) => id !== subscriptionId)
          : [...currentExpanded, subscriptionId],
      };
    });
  };

  const queryClient = useQueryClient();
  const itemsCurrentOrder = useMemo((): IBillingOrderCurrentItem[] => {
    if (!currentOrder?.selectedMilestones) return [];

    interface MilestoneGroup {
      milestones: any[];
      orderItem: any;
    }

    const milestonesBysubscription = currentOrder.selectedMilestones.reduce(
      (acc: Record<string, MilestoneGroup>, milestone: any) => {
        const subscriptionId = milestone.orderItem.subscription || milestone.subscription;
        if (!acc[subscriptionId]) {
          acc[subscriptionId] = {
            milestones: [],
            orderItem: milestone.orderItem,
          };
        }
        acc[subscriptionId].milestones.push(milestone);
        return acc;
      },
      {}
    );

    const uniqueItems = Object.values(milestonesBysubscription).map(
      (subscriptionData: MilestoneGroup): IBillingOrderCurrentItem => {
        const { milestones, orderItem } = subscriptionData;

        const { totalAmount, totalQuantity } = milestones.reduce(
          (acc: { totalAmount: number; totalQuantity: number }, milestone: any) => ({
            totalAmount: acc.totalAmount + (milestone.total || 0),
            totalQuantity: acc.totalQuantity + (milestone.orderItem.quantity || 0),
          }),
          { totalAmount: 0, totalQuantity: 0 }
        );

        return {
          _id: orderItem._id,
          orderItem,
          product: orderItem.product,
          subscription: orderItem.subscription,
          plan: orderItem.plan,
          quantity: orderItem.quantity,
          totalQuantity,
          milestone: {
            ...milestones[0],
            total: totalAmount,
          },
          allMilestones: milestones,
        };
      }
    );

    return uniqueItems.sort((a, b) => a._id.localeCompare(b._id));
  }, [currentOrder]);

  const transformedLegalEntities = useMemo(() => {
    if (!Array.isArray(currentSalesOrder) || currentSalesOrder.length === 0) {
      return [];
    }

    return currentSalesOrder.map((item) => ({
      _id: item._id,
      label: item.label || item.legal_name,
      legal_name: item.legal_name,
      tax_number: item.tax_number,
      documents: item.documents || [],
    }));
  }, [currentSalesOrder]);

  const initItems = useMemo(() => {
    return transformedLegalEntities.map(() => ({
      value: itemsCurrentOrder.map(() => 0),
      amount: itemsCurrentOrder.map(() => 0),
      itemId: itemsCurrentOrder.map((item) => item._id || ''),
      subscription: itemsCurrentOrder.map((item) => item.subscription || ''),
      milestones: itemsCurrentOrder.map(
        (item) => item.allMilestones?.map((m: MilestoneData) => m._id) || []
      ),
    }));
  }, [transformedLegalEntities, itemsCurrentOrder]);
  const defaultValues = useMemo(() => {
    let existingDiscounts: { amount: number; description: string }[] = [];
    let enableDiscounts = false;

    if (currentOrder?.orderDiscounts && currentOrder.orderDiscounts.length > 0) {
      existingDiscounts = currentOrder.orderDiscounts;
      enableDiscounts = true;
    } else if (Array.isArray(currentSalesOrder) && currentSalesOrder.length > 0) {
      const firstSalesOrder = currentSalesOrder[0];
      if (firstSalesOrder?.discounts && firstSalesOrder.discounts.length > 0) {
        existingDiscounts = firstSalesOrder.discounts;
        enableDiscounts = true;
      }
    }

    return {
      legalEntity: transformedLegalEntities,
      items: initItems,
      unitMeasure:
        Array.isArray(currentSalesOrder) && currentSalesOrder.length > 0
          ? currentSalesOrder[0].items?.map((item: any) => item.unitMeasure)
          : itemsCurrentOrder.map(() => ''),
      enableDiscounts,
      discounts: existingDiscounts,
      currentDiscount: {
        amount: 0,
        description: '',
      },
    };
  }, [currentOrder, transformedLegalEntities, initItems, itemsCurrentOrder]);

  const { data: legalEntities, isLoading: isLoadingLegalEntities } = useQuery(
    ['legalEntities', currentOrganization?._id],
    async () => {
      if (currentOrganization?._id) {
        const response = await axios.get(`legal-entities/all`, {
          params: { organization: currentOrganization._id },
        });
        return response.data.data;
      }
      return [];
    },
    {
      enabled: !!currentOrganization?._id,
    }
  );

  const getQuantityToUse = useCallback(
    (item: IBillingOrderCurrentItem): number =>
      item.allMilestones && item.allMilestones.length > 1 ? item.totalQuantity : item.quantity,
    []
  );

  const methods = useForm({
    resolver: yupResolver<any>(
      createBillingOrderSecondStepSchema(
        t,
        {},
        itemsCurrentOrder,
        getQuantityToUse,
        getTotal(currentOrder?.selectedMilestones || [])
      )
    ),
    defaultValues,
  });

  const { watch, handleSubmit, setValue, getValues, reset } = methods;

  const watchAllFields = watch();

  const calculateTotals = useCallback(() => {
    const subtotal = getTotal(currentOrder?.selectedMilestones || []);
    const totalDiscountAmount = watchAllFields.enableDiscounts
      ? (watchAllFields.discounts || []).reduce(
          (sum: number, discount: { amount: number }) => sum + (discount.amount || 0),
          0
        )
      : 0;
    const total = Math.max(0, subtotal - totalDiscountAmount);

    return {
      subtotal,
      totalDiscountAmount,
      total,
    };
  }, [currentOrder?.selectedMilestones, watchAllFields.enableDiscounts, watchAllFields.discounts]);

  useEffect(() => {
    const newSchema = createBillingOrderSecondStepSchema(
      t,
      watchAllFields,
      itemsCurrentOrder,
      getQuantityToUse,
      calculateTotals().subtotal
    );
  }, [watchAllFields, itemsCurrentOrder, getQuantityToUse, calculateTotals, t]);

  const watchActualLegalEntities = watchAllFields.legalEntity;

  const { mutate, isLoading } = useMutation({
    mutationFn: async (data: FormData) => {
      const validator = checkEquivalenceBetweenItems(data.items, itemsCurrentOrder);
      if (validator.length === 0) {
        const finalData = transformInputData(data);

        const legalEntitySums: { total: number; legalEntity: string }[] = [];
        finalData.forEach((entity: TransformedLegalEntity) => {
          const totalAmount = entity.totalAmount;
          legalEntitySums.push({ legalEntity: entity.legal_name, total: totalAmount });
        });

        const errorTotals = Object.values(legalEntitySums).some((totalData) => {
          if (totalData.total === 0) {
            enqueueSnackbar(
              `${totalData.legalEntity}: ${t(`The total to be billed must be greater than zero`)}`,
              {
                variant: 'error',
                autoHideDuration: 5000,
              }
            );
            return true;
          }
          return false;
        });
        if (!errorTotals) {
          if (data.onlySave) {
            const orderData = {
              organization: currentOrder?.organization?._id ?? null,
              contacts: currentOrder?.contacts ?? null,
              documents: currentOrder?.documents ?? [],
              items: currentOrder?.items ?? null,
              itemsToRenewal: currentOrder?.itemsToRenewal ?? null,
              itemsToUpgrade: currentOrder?.itemsToUpgrade ?? null,
              itemsToAddLicenses: currentOrder?.itemsToAddLicenses ?? null,
              acceptedTermsAndConditions: currentOrder?.acceptedTermsAndConditions ?? null,
              status: currentOrder?.status ?? ChoicesOrderStatus.created,
              isMarket: currentOrder?.isMarket ?? null,
              onlySave: data?.onlySave ?? null,
              milestones: currentOrder?.milestones || [],
              selectedMilestones: currentOrder?.selectedMilestones || [],
              orderType: OrderTypes.BILLING_ORDER,
              discounts:
                data.enableDiscounts && data.discounts && data.discounts.length > 0
                  ? data.discounts
                  : null,
            };
            if (currentOrder?._id) {
              await axios.patch(`orders/${currentOrder?._id}`, {
                order: orderData,
                salesOrders: finalData,
              });
            } else {
              await axios.post(`orders`, { order: orderData, salesOrders: finalData });
            }
            enqueueSnackbar(t('Update success!'));

            await queryClient.invalidateQueries({
              queryKey: ['billing-order', currentOrder?._id, 'edit'],
            });
            await queryClient.invalidateQueries({ queryKey: ['billing-orders'] });
            await queryClient.invalidateQueries({
              queryKey: ['organizations', currentOrder?.organization?._id],
            });

            reset();
            onCancel();
          }

          onNext({
            salesOrders: finalData as any,
            discounts:
              data.enableDiscounts && data.discounts && data.discounts.length > 0
                ? data.discounts
                : null,
          });
        }
      } else {
        let message: string;
        const errorItems = validator
          .map((item: EquivalenceError) => item.itemIndex)
          .join(', item ');
        switch (validator.length) {
          case 1:
            message = `${t(`There is an error in the distribution of item`)} ${errorItems}`;
            break;
          default:
            message = `${t(`There is an error in the distribution, in item`)} ${errorItems}`;
            break;
        }
        enqueueSnackbar(`${message}`, {
          variant: 'error',
          autoHideDuration: 5000,
        });
      }
    },
    onError: (e) => {
      setError(e as FormError);
      dialogError.onTrue();
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      mutate(data);
    } catch (e) {}
  });

  const addDiscount = useCallback(() => {
    const currentDiscount = watchAllFields.currentDiscount;
    const subtotal = calculateTotals().subtotal;
    const currentDiscounts = watchAllFields.discounts || [];

    if (!currentDiscount?.amount || currentDiscount.amount <= 0) {
      enqueueSnackbar(t('Please enter a valid discount amount'), { variant: 'error' });
      return;
    }

    const totalExistingDiscounts = currentDiscounts.reduce(
      (sum: number, discount: { amount: number }) => sum + (discount.amount || 0),
      0
    );

    if (totalExistingDiscounts + currentDiscount.amount > subtotal) {
      enqueueSnackbar(t('Total discounts cannot exceed the subtotal'), { variant: 'error' });
      return;
    }

    if (!currentDiscount?.description || currentDiscount.description.trim().length < 5) {
      enqueueSnackbar(t('Please provide a valid discount description (at least 5 characters)'), {
        variant: 'error',
      });
      return;
    }

    const newDiscounts = [
      ...currentDiscounts,
      {
        amount: Number(currentDiscount.amount),
        description: String(currentDiscount.description).trim(),
      },
    ];

    setValue('discounts', newDiscounts);

    setValue('currentDiscount.amount', 0);
    setValue('currentDiscount.description', '');

    enqueueSnackbar(t('Discount added successfully'), { variant: 'success' });
  }, [
    watchAllFields.currentDiscount,
    watchAllFields.discounts,
    calculateTotals,
    setValue,
    enqueueSnackbar,
    t,
  ]);

  const removeDiscount = useCallback(
    (index: number) => {
      const currentDiscounts = watchAllFields.discounts || [];
      const newDiscounts = currentDiscounts.filter(
        (_discount: { amount: number; description: string }, i: number) => i !== index
      );
      setValue('discounts', newDiscounts);
      enqueueSnackbar(t('Discount removed'), { variant: 'info' });
    },
    [watchAllFields.discounts, setValue, enqueueSnackbar, t]
  );

  const calculateAmount = useCallback(
    (index: number, pos: number, value: number) => {
      const initPath = `items[${index}].value[${pos}]`;
      const fieldPath = `items[${index}].amount[${pos}]`;
      const unitsMeasure = watchAllFields.unitMeasure;
      const item = itemsCurrentOrder[pos];

      if (!item || !item.milestone) {
        setValue(initPath, value);
        setValue(fieldPath, 0);
        setValue(`items[${index}].itemId[${pos}]`, item?._id);
        setValue(`items[${index}].subscription[${pos}]`, item?.subscription);
        setValue(
          `items[${index}].milestones[${pos}]`,
          item?.allMilestones?.map((m: any) => m._id) || []
        );
        return;
      }

      const unitMeasure = unitsMeasure[pos] || ChoicesOrderUnitMeasure.quantity;
      let calculatedValue = value;

      if (item.allMilestones && item.allMilestones.length > 1) {
        switch (unitMeasure) {
          case ChoicesOrderUnitMeasure.quantity: {
            const sortedMilestones = [...item.allMilestones].sort(
              (a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime()
            );

            const totalConsumedQuantity = Array.from({ length: index }, (_, i) => {
              const previousValue = watchAllFields?.items?.[i]?.value?.[pos] || 0;
              return Number(previousValue);
            }).reduce((sum, val) => sum + val, 0);

            const result = sortedMilestones.reduce(
              (acc, milestone) => {
                if (acc.remainingQuantity <= 0) return acc;

                const milestoneQuantity = milestone.orderItem.quantity || 0;
                const milestoneTotal = milestone.total || 0;

                let milestoneConsumed = 0;
                if (acc.totalConsumedQuantity > 0) {
                  milestoneConsumed = Math.min(acc.totalConsumedQuantity, milestoneQuantity);
                  acc.totalConsumedQuantity -= milestoneConsumed;
                }

                const availableInMilestone = milestoneQuantity - milestoneConsumed;

                if (availableInMilestone <= 0) return acc;

                const quantityToUse = Math.min(acc.remainingQuantity, availableInMilestone);
                const proportionalAmount =
                  milestoneQuantity > 0 ? (milestoneTotal / milestoneQuantity) * quantityToUse : 0;

                return {
                  totalAmount: acc.totalAmount + proportionalAmount,
                  remainingQuantity: acc.remainingQuantity - quantityToUse,
                  totalConsumedQuantity: acc.totalConsumedQuantity,
                };
              },
              {
                totalAmount: 0,
                remainingQuantity: value,
                totalConsumedQuantity,
              }
            );

            calculatedValue = result.totalAmount;
            break;
          }
          case ChoicesOrderUnitMeasure.percentageAmount: {
            const totalMilestoneAmount = item.allMilestones.reduce(
              (sum: number, milestone: any) => sum + (milestone.total || 0),
              0
            );
            calculatedValue = totalMilestoneAmount * (value / 100);
            break;
          }
          default:
            calculatedValue = value;
            break;
        }
      } else {
        const total = Number(item.milestone.total?.toFixed(2) ?? 0);
        const itemQuantity = item.quantity ?? 0;

        switch (unitMeasure) {
          case ChoicesOrderUnitMeasure.quantity:
            calculatedValue = itemQuantity > 0 ? (total / itemQuantity) * value : 0;
            break;
          case ChoicesOrderUnitMeasure.percentageAmount:
            calculatedValue = total * (value / 100);
            break;
          default:
            calculatedValue = value;
            break;
        }
      }

      setValue(initPath, value);
      setValue(fieldPath, calculatedValue);
      setValue(`items[${index}].itemId[${pos}]`, item._id);
      setValue(`items[${index}].subscription[${pos}]`, item.subscription);
      setValue(
        `items[${index}].milestones[${pos}]`,
        item.allMilestones?.map((m: any) => m._id) || [item.milestone._id]
      );
    },
    [itemsCurrentOrder, setValue, watchAllFields.unitMeasure, watchAllFields.items]
  );
  const groupMilestonesBysubscription = useCallback(
    (milestones: any[]) => {
      interface subscriptionGroupData {
        subscription: any;
        milestones: (any & { originalIndex: number })[];
        minOriginalIndex: number;
      }

      const grouped = milestones.reduce(
        (acc, milestone) => {
          const subscriptionId = milestone.orderItem?.subscription || milestone?.subscription;
          const originalIndex = itemsCurrentOrder.findIndex(
            (item) => item._id === milestone.orderItem._id
          );

          if (!acc[subscriptionId]) {
            acc[subscriptionId] = {
              subscription: milestone.orderItem.subscription,
              milestones: [],
              minOriginalIndex: originalIndex,
            };
          }
          acc[subscriptionId].milestones.push({ ...milestone, originalIndex });
          acc[subscriptionId].minOriginalIndex = Math.min(
            acc[subscriptionId].minOriginalIndex,
            originalIndex
          );
          return acc;
        },
        {} as Record<string, subscriptionGroupData>
      );

      return Object.entries(grouped)
        .sort(
          ([, a], [, b]) =>
            (a as subscriptionGroupData).minOriginalIndex -
            (b as subscriptionGroupData).minOriginalIndex
        )
        .map(([subscriptionId, data]) => [
          subscriptionId,
          {
            subscription: (data as subscriptionGroupData).subscription,
            milestones: (data as subscriptionGroupData).milestones,
          },
        ]) as [string, { subscription: any; milestones: any[] }][];
    },
    [itemsCurrentOrder]
  );

  const recalculateAmountsForAllEntities = useCallback(() => {
    const currentLegalEntities = getValues('legalEntity') || [];
    const currentItems = getValues('items') || [];
    const currentUnitMeasures = getValues('unitMeasure') || [];

    currentLegalEntities.forEach((entity: Entity, entityIndex: number) => {
      itemsCurrentOrder.forEach((item, itemIndex) => {
        const currentValue = currentItems[entityIndex]?.value?.[itemIndex];
        if (currentValue !== undefined && currentValue !== '' && currentUnitMeasures[itemIndex]) {
          calculateAmount(entityIndex, itemIndex, Number(currentValue));
        }
      });
    });
  }, [getValues, itemsCurrentOrder, calculateAmount]);

  const handleAddDocument = (legalEntityId: string) => {
    setDocumentLegalEntityId(legalEntityId);
    setCurrentEditingDocument(null);
    setTimeout(() => {
      openUploadFile.onTrue();
    }, 200);
  };

  const handleDeleteDocument = async (documentId: string, legalEntityId: string) => {
    try {
      if (documentId) {
        await axios.delete(`/order-documents/${documentId}`);
      }

      const legalEntityIndex = watchActualLegalEntities.findIndex(
        (entity: Entity) => entity._id === legalEntityId
      );

      if (legalEntityIndex !== -1) {
        const currentDocuments = watchActualLegalEntities[legalEntityIndex]?.documents || [];
        const updatedDocuments = currentDocuments.filter(
          (doc: IOrderDocument) => doc._id !== documentId
        );

        setValue(`legalEntity[${legalEntityIndex}].documents`, updatedDocuments, {
          shouldValidate: true,
        });

        enqueueSnackbar(t('Document deleted successfully'), {
          variant: 'success',
        });
      }
    } catch (err) {
      enqueueSnackbar(t('Error deleting document'), {
        variant: 'error',
      });
    }
  };

  const unitMesureChoices = [
    { value: ChoicesOrderUnitMeasure.quantity, label: t('Quantity') },
    {
      value: ChoicesOrderUnitMeasure.percentageAmount,
      label: t('Percentage of the amount'),
    },
    { value: ChoicesOrderUnitMeasure.amount, label: t('Amount') },
  ];

  const unitMesureChoicesHelpText: Record<ChoicesOrderUnitMeasure, string> = {
    [ChoicesOrderUnitMeasure.quantity]: t('Enter the quantity to be billed'),
    [ChoicesOrderUnitMeasure.percentageAmount]: t('Enter the percentage to be billed'),
    [ChoicesOrderUnitMeasure.amount]: t('Enter the amount to be billed'),
  };

  useEffect(() => {
    const { legalEntity = [] } = watchAllFields || {};

    if (legalEntity.length !== prevLegalEntityCount) {
      if (legalEntity.length === 1 && itemsCurrentOrder) {
        setValue(
          'unitMeasure',
          Array(itemsCurrentOrder.length).fill(ChoicesOrderUnitMeasure.quantity)
        );

        requestAnimationFrame(() => {
          itemsCurrentOrder.forEach((item, index) => {
            const quantityToUse = getQuantityToUse(item);

            if (quantityToUse) {
              calculateAmount(0, index, quantityToUse);
            }
          });
        });
      } else if (prevLegalEntityCount === 1 && legalEntity.length > 1) {
        itemsCurrentOrder.forEach((_, index) => {
          if (!watchAllFields.unitMeasure[index]) {
            setValue(`unitMeasure[${index}]`, ChoicesOrderUnitMeasure.quantity);
          }

          legalEntity.forEach((entity: Entity, entityIndex: number) => {
            setValue(`items[${entityIndex}].value[${index}]`, '');
            setValue(`items[${entityIndex}].amount[${index}]`, '');
            setValue(
              `items[${entityIndex}].subscription[${index}]`,
              itemsCurrentOrder[index]?.subscription || ''
            );
            setValue(
              `items[${entityIndex}].milestones[${index}]`,
              itemsCurrentOrder[index]?.allMilestones?.map((m: any) => m._id) || []
            );
          });
        });

        requestAnimationFrame(() => {
          recalculateAmountsForAllEntities();
        });
      } else if (legalEntity.length > prevLegalEntityCount && prevLegalEntityCount >= 1) {
        const currentItems = getValues('items') || [];
        if (currentItems.length < legalEntity.length) {
          for (let i = currentItems.length; i < legalEntity.length; i += 1) {
            itemsCurrentOrder.forEach((item, j) => {
              setValue(`items[${i}].value[${j}]`, '');
              setValue(`items[${i}].amount[${j}]`, '');
              setValue(`items[${i}].subscription[${j}]`, item?.subscription || '');
              setValue(
                `items[${i}].milestones[${j}]`,
                item?.allMilestones?.map((m: any) => m._id) || []
              );
            });
          }
        }

        requestAnimationFrame(() => {
          recalculateAmountsForAllEntities();
        });
      }

      setPrevLegalEntityCount(legalEntity.length);
    }
  }, [
    watchAllFields.legalEntity.length,
    watchAllFields.unitMeasure,
    itemsCurrentOrder,
    setValue,
    calculateAmount,
    getValues,
    prevLegalEntityCount,
    watchAllFields,
    getQuantityToUse,
    recalculateAmountsForAllEntities,
  ]);

  useEffect(() => {
    if (legalEntities && legalEntities.length === 1 && itemsCurrentOrder) {
      itemsCurrentOrder.forEach((item, index) => {
        const quantityToUse = getQuantityToUse(item);
        if (quantityToUse) {
          calculateAmount(0, index, quantityToUse);
        }
      });

      const newValues = {
        legalEntity: [
          {
            _id: legalEntities[0]._id,
            label: legalEntities[0].legal_name,
            legal_name: legalEntities[0].legal_name,
            tax_number: legalEntities[0].tax_number,
          },
        ],
        unitMeasure: Array(itemsCurrentOrder.length).fill(ChoicesOrderUnitMeasure.quantity),
      };

      reset({ ...getValues(), ...newValues }, { keepErrors: true, keepDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalEntities]);

  useEffect(() => {
    if (itemsCurrentOrder && itemsCurrentOrder.length > 0) {
      if (!currentSalesOrder) {
        setValue(
          'unitMeasure',
          itemsCurrentOrder.map(() => '')
        );
      }
    }
  }, [itemsCurrentOrder, setValue, currentSalesOrder]);

  useEffect(() => {
    const currentLegalEntities = getValues('legalEntity') || [];
    const currentUnitMeasures = getValues('unitMeasure') || [];

    if (currentLegalEntities.length > 0 && currentUnitMeasures.length > 0) {
      requestAnimationFrame(() => {
        recalculateAmountsForAllEntities();
      });
    }
  }, [watchAllFields.unitMeasure, getValues, recalculateAmountsForAllEntities]);

  useEffect(() => {
    if (milestonesResetTrigger > 0) {
      reset(defaultValues);
      setExpandedRows({});
      setPrevLegalEntityCount(0);
    }
  }, [milestonesResetTrigger, defaultValues, reset]);

  useEffect(() => {
    if (milestonesResetTrigger > 0) {
      setValue('currentDiscount.amount', 0);
      setValue('currentDiscount.description', '');
    }
  }, [milestonesResetTrigger, setValue]);

  useEffect(() => {
    if (!watchAllFields.enableDiscounts) {
      setValue('discounts', []);
      setValue('currentDiscount.amount', 0);
      setValue('currentDiscount.description', '');
    }
  }, [watchAllFields.enableDiscounts, setValue]);

  return (
    <>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardHeader title={t('Billing Distribution')} />
          <CardContent>
            <Grid container spacing={1} sx={{ pt: 0 }}>
              <Grid xs={12}>
                <RHFAutocomplete
                  name="legalEntity"
                  label={t('Legal Entity')}
                  multiple
                  options={
                    legalEntities?.map(
                      (item: { _id: string; legal_name: string; tax_number: string }) => ({
                        _id: item._id,
                        label: `${item.legal_name} (${item.tax_number || ' '})`,
                        legal_name: item.legal_name,
                        tax_number: item.tax_number,
                      })
                    ) ?? []
                  }
                  onChange={(event, newValue: any) => {
                    const currentLegalEntity = getValues('legalEntity') ?? [];
                    const currentItems = getValues('items') ?? [];

                    const removedIndices = currentLegalEntity
                      .map((item: Entity, index: number) =>
                        newValue.some((newItem: any) => newItem._id === item._id) ? -1 : index
                      )
                      .filter((index: number) => index !== -1);

                    if (removedIndices.length > 0) {
                      currentItems.splice(removedIndices, 1);
                      setValue('items', currentItems);
                    }
                    setValue('legalEntity', newValue);

                    requestAnimationFrame(() => {
                      recalculateAmountsForAllEntities();
                    });
                  }}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  loading={isLoadingLegalEntities}
                />
              </Grid>

              <Grid xs={12}>
                <Button
                  onClick={() => {
                    openCreateLegalEntity.onTrue();
                  }}
                  color="primary"
                  startIcon={<Iconify icon="mingcute:add-line" />}
                >
                  {t('New Legal Entity')}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <ShowIf condition={watchActualLegalEntities.length > 1}>
          <Card sx={{ mb: 2 }}>
            <CardHeader title={t('Distribution unit')} />
            <CardContent>
              <Grid container spacing={1} sx={{ pt: 0 }}>
                {itemsCurrentOrder?.map((item: IBillingOrderCurrentItem, index) => (
                  <React.Fragment key={item._id}>
                    <Grid xs={8} alignContent="center">
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {`${item?.product?.name || item?.product?.label} - ${item?.milestone?.orderItem?.sku?.name ?? ''} - ${item?.milestone?.orderItem?.plan?.name ?? ''}`}
                      </Typography>
                    </Grid>
                    <Grid xs={4}>
                      <RHFSelect
                        sx={{ mb: 1 }}
                        name={`unitMeasure[${index}]`}
                        label={t('Distribution unit')}
                        size="small"
                      >
                        {unitMesureChoices.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </RHFSelect>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </ShowIf>

        <ShowIf condition={watchActualLegalEntities.length > 0}>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {watchActualLegalEntities?.map((legalEntity: Entity, legalIndex: number) => (
                <Box key={legalEntity._id}>
                  <BillingOrderLegalEntityBillingItem
                    legalEntity={legalEntity}
                    legalIndex={legalIndex}
                    itemsCurrentOrder={itemsCurrentOrder}
                    watchAllFields={watchAllFields}
                    unitMesureChoicesHelpText={unitMesureChoicesHelpText}
                    calculateAmount={calculateAmount}
                    groupMilestonesBysubscription={groupMilestonesBysubscription}
                    expandedRows={expandedRows}
                    toggleRow={toggleRow}
                    currentOrder={currentOrder}
                    onAddDocument={(legalEntityId: string) => handleAddDocument(legalEntityId)}
                    onDeleteDocument={handleDeleteDocument}
                  />
                  {legalIndex < watchActualLegalEntities.length - 1 && <Divider sx={{ my: 2 }} />}
                </Box>
              ))}

              <Divider />
              <Box sx={{ px: 2, pb: 2 }}>
                <Grid container spacing={2} sx={{ pt: 2 }}>
                  <Grid xs={12}>
                    <RHFCheckbox name="enableDiscounts" label={t('Add Discount')} />
                  </Grid>

                  <ShowIf condition={watchAllFields.enableDiscounts}>
                    <Grid xs={12} md={6}>
                      <RHFNumberField
                        name="currentDiscount.amount"
                        label={t('Amount')}
                        thousandsGroupStyle="thousand"
                        thousandSeparator
                        decimalScale={2}
                        prefix="$ "
                        allowNegative={false}
                        inputProps={{ autoComplete: 'off' }}
                        helperText={
                          calculateTotals
                            ? t('Enter the discount amount (Max: ${{max}})', {
                                max: (
                                  calculateTotals().subtotal - calculateTotals().totalDiscountAmount
                                ).toFixed(2),
                              })
                            : ''
                        }
                      />
                    </Grid>
                    <Grid xs={12} md={6}>
                      <RHFTextField
                        name="currentDiscount.description"
                        label={t('Description')}
                        placeholder={t('Description of the discount')}
                        multiline
                        rows={2}
                        helperText={t('Please provide a description for the discount')}
                      />
                    </Grid>

                    <Grid xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => {
                          setValue('enableDiscounts', false);
                          setValue('currentDiscount.amount', 0);
                          setValue('currentDiscount.description', '');
                        }}
                      >
                        {t('Cancel')}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={addDiscount}
                        disabled={
                          !watchAllFields.currentDiscount?.amount ||
                          !watchAllFields.currentDiscount?.description
                        }
                      >
                        {t('Add Discount')}
                      </Button>
                    </Grid>

                    <ShowIf
                      condition={watchAllFields.discounts && watchAllFields.discounts.length > 0}
                    >
                      <Grid xs={12}>
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                          {t('Applied Discounts')}
                        </Typography>
                        {watchAllFields.discounts.map(
                          (discount: { amount: number; description: string }, index: number) => (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 2,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 1,
                                bgcolor: 'background.paper',
                              }}
                            >
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body1">
                                  <strong>$ {discount.amount.toFixed(2)}</strong> -{' '}
                                  {discount.description}
                                </Typography>
                              </Box>
                              <IconButton
                                onClick={() => removeDiscount && removeDiscount(index)}
                                color="error"
                                size="small"
                              >
                                <Iconify icon="mingcute:delete-2-line" />
                              </IconButton>
                            </Box>
                          )
                        )}
                      </Grid>
                    </ShowIf>
                  </ShowIf>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </ShowIf>

        <ShowIf condition={watchActualLegalEntities.length > 0}>
          <Divider sx={{ my: 3 }} />
          <Card sx={{ mb: 2 }}>
            <CardHeader title={t('Order Summary')} />
            <CardContent>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t('Subtotal')}: $ {calculateTotals().subtotal.toFixed(2)}
                </Typography>
                <ShowIf
                  condition={
                    watchAllFields.enableDiscounts &&
                    watchAllFields.discounts &&
                    watchAllFields.discounts.length > 0
                  }
                >
                  {watchAllFields.discounts.map(
                    (discount: { amount: number; description: string }, index: number) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{ mb: 0.5, color: 'text.secondary' }}
                      >
                        {t('Discount')} ({discount.description}) - $ {discount.amount.toFixed(2)}
                      </Typography>
                    )
                  )}
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}
                  >
                    {t('Total Discounts')}: - $ {calculateTotals().totalDiscountAmount.toFixed(2)}
                  </Typography>
                </ShowIf>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', borderTop: 1, borderColor: 'divider', pt: 1, mb: 2 }}
                >
                  {t('Total')}: $ {calculateTotals().total.toFixed(2)}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {t(`Total to be invoiced $ {{Total}}`, {
                    Total: calculateTotals().total.toFixed(2),
                  })}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </ShowIf>

        <Grid container>
          <Grid xs={4} sx={{ textAlign: 'left' }}>
            <LoadingButton
              color="primary"
              type="submit"
              onClick={onBack}
              variant="contained"
              sx={{ mr: 1 }}
              loading={isLoading}
            >
              {t('Back')}
            </LoadingButton>

            <Button
              variant="outlined"
              sx={{ mr: 1 }}
              onClick={() => {
                onCancel();
              }}
            >
              {t('Cancel')}
            </Button>
          </Grid>

          <Grid
            xs={8}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <LoadingButton
              color="secondary"
              type="button"
              onClick={() => {
                setValue('onlySave', true);
                onSubmit();
              }}
              sx={{ mr: 1 }}
              variant="contained"
              loading={isLoading}
            >
              {t('Save Draft')}
            </LoadingButton>
            <LoadingButton color="primary" type="submit" variant="contained" loading={isLoading}>
              {t('Next')}
            </LoadingButton>
          </Grid>
        </Grid>
      </FormProvider>
      {/* Upload files */}
      {openUploadFile.value && !!documentLegalEntityId && (
        <OrderUploadOVDialog
          open={openUploadFile.value}
          onClose={() => {
            openUploadFile.onFalse();
          }}
          organizationId={currentOrganization._id}
          setExternalValue={setValue}
          index={watchActualLegalEntities.findIndex(
            (legalEntity: Entity) => legalEntity._id === documentLegalEntityId
          )}
          getValues={getValues}
          currentDocument={currentEditingDocument}
          sx={{ zIndex: 1300 }}
          onSave={(document) => {
            return document;
          }}
        />
      )}
      {/* Create legal entity */}
      {openCreateLegalEntity.value && (
        <OrderLegalEntityNewEditForm
          open={openCreateLegalEntity.value}
          onClose={openCreateLegalEntity.onFalse}
          organization={currentOrganization}
        />
      )}
      <DialogError
        title={t('Error Creating Order')}
        error={error}
        open={dialogError.value}
        onClose={dialogError.onFalse}
      />
    </>
  );
};
